/**
 * 学习目标规划计算模块
 */

import { CardType, CardMode, StudyGoal, CardRecord, MyIELTSDatabase, defaultDb } from '../db/indexedDb';

/** 各类型卡片数量统计 */
export interface CardCounts {
  speakingPart1: number;
  speakingPart2: number;
  speakingPart3: number;
  writing: number;
  listening: number;
  vocab: number;
}

/** 目标计算结果 */
export interface GoalCalculation {
  // 卡片统计
  totalCards: number;
  cardsByType: {
    speakingPart1: number;
    speakingPart2: number;
    speakingPart3: number;
    writing: number;
    listening: number;
    vocab: number;
  };

  // 时间统计（分钟）- 仅首次学习总时间
  firstPassMinutes: number;
  reviewMinutes: number;  // 保留用于显示预估
  totalMinutes: number;   // 保留用于显示预估

  // 每日目标（基于实际）
  daysLeft: number;
  dailyNewCards: number;      // 每日新卡片数
  dailyDueCards: number;      // 今日 due 卡片数
  dailyCards: number;         // 总计 = 新 + due
  dailyNewMinutes: number;    // 新卡片时间
  dailyDueMinutes: number;    // 复习时间
  dailyMinutes: number;       // 总时间
  dailyCardsByType: Partial<Record<CardType, number>>;

  // 进度（基于首次学习）
  completedCards: number;     // 已首次学习的卡片数
  progressPercent: number;    // 进度百分比

  // 与理想进度对比
  idealProgressPercent: number;  // 理想进度百分比
  daysDiff: number;              // 正数=超前，负数=落后
}

/** 今日学习进度 */
export interface TodayProgress {
  cards: number;
  minutes: number;
  byType: Partial<Record<CardType, number>>;
}

/** 默认目标配置 */
export const DEFAULT_STUDY_GOAL: StudyGoal = {
  targetDate: Date.now() + 20 * 24 * 60 * 60 * 1000, // 20天后
  createdAt: Date.now(),
  updatedAt: Date.now(),
  scope: {
    speaking: {
      enabled: true,
      parts: ['Part 1', 'Part 2', 'Part 3'],
      ratio: 34,  // 默认 34%
    },
    writing: {
      enabled: true,
      modes: ['translation'],
      ratio: 100, // 默认 100%
    },
    listening: { enabled: false },
    vocab: { enabled: false },
  },
  timeEstimates: {
    speakingPart1: 3,
    speakingPart2: 7,
    speakingPart3: 3,
    writing: 4,
    listening: 2,
    vocab: 1,
  },
  reviewSettings: {
    includeReview: true,
    speaking: {
      reviewTimes: 2,      // 口语平均复习 2 次
      reviewRatio: 0.5,
    },
    writing: {
      reviewTimes: 4,      // 写作复习 4 次
      reviewRatio: 0.5,
    },
  },
  speakingPartRatio: {
    part1: 5,
    part2: 2,
    part3: 3,
  },
};

/**
 * 统计数据库中各类型卡片数量
 */
export async function getCardCounts(db: MyIELTSDatabase = defaultDb): Promise<CardCounts> {
  const allCards = await db.cards.toArray();

  const speakingCards = allCards.filter(c => c.type === 'speaking');
  const speakingPart1 = speakingCards.filter(c => (c.content as Record<string, unknown>)?.part === 'Part 1').length;
  const speakingPart2 = speakingCards.filter(c => (c.content as Record<string, unknown>)?.part === 'Part 2').length;
  const speakingPart3 = speakingCards.filter(c => (c.content as Record<string, unknown>)?.part === 'Part 3').length;

  const writing = allCards.filter(c => c.type === 'writing' && c.mode === 'translation').length;
  const listening = allCards.filter(c => c.type === 'listening').length;
  const vocab = allCards.filter(c => c.type === 'vocab').length;

  return { speakingPart1, speakingPart2, speakingPart3, writing, listening, vocab };
}

/**
 * 统计已完成卡片数量（已学过至少一次）
 */
export async function getCompletedCounts(
  goal: StudyGoal,
  db: MyIELTSDatabase = defaultDb
): Promise<CardCounts> {
  const allCards = await db.cards.toArray();

  // 已学过 = history 有记录
  const learned = allCards.filter(c => c.history && c.history.length > 0);

  const speakingCards = learned.filter(c => c.type === 'speaking');
  let speakingPart1 = 0, speakingPart2 = 0, speakingPart3 = 0;

  if (goal.scope.speaking.enabled) {
    if (goal.scope.speaking.parts.indexOf('Part 1') >= 0) {
      speakingPart1 = speakingCards.filter(c => (c.content as Record<string, unknown>)?.part === 'Part 1').length;
    }
    if (goal.scope.speaking.parts.indexOf('Part 2') >= 0) {
      speakingPart2 = speakingCards.filter(c => (c.content as Record<string, unknown>)?.part === 'Part 2').length;
    }
    if (goal.scope.speaking.parts.indexOf('Part 3') >= 0) {
      speakingPart3 = speakingCards.filter(c => (c.content as Record<string, unknown>)?.part === 'Part 3').length;
    }
  }

  let writing = 0;
  if (goal.scope.writing.enabled) {
    writing = learned.filter(c =>
      c.type === 'writing' && goal.scope.writing.modes.indexOf(c.mode as CardMode) >= 0
    ).length;
  }

  const listening = goal.scope.listening.enabled
    ? learned.filter(c => c.type === 'listening').length
    : 0;

  const vocab = goal.scope.vocab.enabled
    ? learned.filter(c => c.type === 'vocab').length
    : 0;

  return { speakingPart1, speakingPart2, speakingPart3, writing, listening, vocab };
}

/** 今日 Due 统计 */
export interface TodayDueInfo {
  totalDue: number;
  speakingDue: number;
  writingDue: number;
  dueMinutes: number;  // 估算复习时间
}

/**
 * 获取今日 due 卡片数量
 */
export async function getTodayDueCount(
  goal: StudyGoal,
  db: MyIELTSDatabase = defaultDb
): Promise<TodayDueInfo> {
  const now = Date.now();
  const allCards = await db.cards.toArray();
  
  // 筛选目标范围内的 due 卡片
  const { scope, timeEstimates, reviewSettings } = goal;
  let speakingDue = 0;
  let writingDue = 0;
  let dueMinutes = 0;
  
  for (const card of allCards) {
    // 检查是否在目标范围内
    if (card.type === 'speaking' && scope.speaking.enabled) {
      const part = (card.content as any)?.part;
      if (scope.speaking.parts.includes(part)) {
        // 检查是否 due（已学习且到期）
        if (card.history && card.history.length > 0 && card.nextReviewDate <= now) {
          speakingDue++;
          const time = part === 'Part 2' ? timeEstimates.speakingPart2 : 
                       part === 'Part 1' ? timeEstimates.speakingPart1 : timeEstimates.speakingPart3;
          dueMinutes += time * reviewSettings.speaking.reviewRatio;
        }
      }
    } else if (card.type === 'writing' && scope.writing.enabled) {
      if (card.history && card.history.length > 0 && card.nextReviewDate <= now) {
        writingDue++;
        dueMinutes += timeEstimates.writing * reviewSettings.writing.reviewRatio;
      }
    }
  }
  
  return {
    totalDue: speakingDue + writingDue,
    speakingDue,
    writingDue,
    dueMinutes: Math.round(dueMinutes),
  };
}

/**
 * 计算目标相关数据
 */
export function calculateGoal(
  goal: StudyGoal,
  totalCounts: CardCounts,
  completedCounts: CardCounts,
  todayDue: TodayDueInfo = { totalDue: 0, speakingDue: 0, writingDue: 0, dueMinutes: 0 },
  now: number = Date.now()
): GoalCalculation {
  const { scope, timeEstimates, reviewSettings, speakingPartRatio } = goal;

  // 计算目标范围内的卡片数（按比例）
  const speakingRatio = (scope.speaking.ratio ?? 100) / 100;
  const writingRatio = (scope.writing.ratio ?? 100) / 100;
  
  const cardsByType = {
    speakingPart1: scope.speaking.enabled && scope.speaking.parts.indexOf('Part 1') >= 0 
      ? Math.ceil(totalCounts.speakingPart1 * speakingRatio) : 0,
    speakingPart2: scope.speaking.enabled && scope.speaking.parts.indexOf('Part 2') >= 0 
      ? Math.ceil(totalCounts.speakingPart2 * speakingRatio) : 0,
    speakingPart3: scope.speaking.enabled && scope.speaking.parts.indexOf('Part 3') >= 0 
      ? Math.ceil(totalCounts.speakingPart3 * speakingRatio) : 0,
    writing: scope.writing.enabled ? Math.ceil(totalCounts.writing * writingRatio) : 0,
    listening: scope.listening.enabled ? totalCounts.listening : 0,
    vocab: scope.vocab.enabled ? totalCounts.vocab : 0,
  };

  const totalCards = Object.keys(cardsByType).reduce((sum, key) => sum + (cardsByType as Record<string, number>)[key], 0);

  // 计算首次学习时间（分开口语和写作）
  const speakingFirstPass =
    cardsByType.speakingPart1 * timeEstimates.speakingPart1 +
    cardsByType.speakingPart2 * timeEstimates.speakingPart2 +
    cardsByType.speakingPart3 * timeEstimates.speakingPart3;
  
  const writingFirstPass = cardsByType.writing * timeEstimates.writing;
  
  const otherFirstPass =
    cardsByType.listening * timeEstimates.listening +
    cardsByType.vocab * timeEstimates.vocab;

  const firstPassMinutes = speakingFirstPass + writingFirstPass + otherFirstPass;

  // 计算复习时间（按类型分开）
  let reviewMinutes = 0;
  if (reviewSettings.includeReview) {
    const speakingReview = Math.round(
      speakingFirstPass * reviewSettings.speaking.reviewRatio * reviewSettings.speaking.reviewTimes
    );
    const writingReview = Math.round(
      writingFirstPass * reviewSettings.writing.reviewRatio * reviewSettings.writing.reviewTimes
    );
    // 其他类型用写作的设置
    const otherReview = Math.round(
      otherFirstPass * reviewSettings.writing.reviewRatio * reviewSettings.writing.reviewTimes
    );
    reviewMinutes = speakingReview + writingReview + otherReview;
  }

  const totalMinutes = firstPassMinutes + reviewMinutes;

  // 计算剩余天数
  const daysLeft = Math.max(1, Math.ceil((goal.targetDate - now) / (24 * 60 * 60 * 1000)));

  // 计算已完成数量（首次学习）
  const completedCards =
    completedCounts.speakingPart1 +
    completedCounts.speakingPart2 +
    completedCounts.speakingPart3 +
    completedCounts.writing +
    completedCounts.listening +
    completedCounts.vocab;

  // 剩余需要首次学习的卡片
  const remainingNewCards = Math.max(0, totalCards - completedCards);

  // 每日新卡片数（基于剩余卡片和剩余天数）
  const dailyNewCards = Math.ceil(remainingNewCards / daysLeft);

  // 今日 due 卡片数
  const dailyDueCards = todayDue.totalDue;

  // 每日总卡片
  const dailyCards = dailyNewCards + dailyDueCards;

  // 计算每日时间
  // 新卡片时间：按各类型比例分配
  const speakingNewRatio = (cardsByType.speakingPart1 + cardsByType.speakingPart2 + cardsByType.speakingPart3) / Math.max(1, totalCards);
  const writingNewRatio = cardsByType.writing / Math.max(1, totalCards);
  const dailyNewSpeaking = Math.ceil(dailyNewCards * speakingNewRatio);
  const dailyNewWriting = Math.ceil(dailyNewCards * writingNewRatio);

  // 新卡片平均时间
  const avgSpeakingTime = cardsByType.speakingPart1 + cardsByType.speakingPart2 + cardsByType.speakingPart3 > 0
    ? (cardsByType.speakingPart1 * timeEstimates.speakingPart1 +
       cardsByType.speakingPart2 * timeEstimates.speakingPart2 +
       cardsByType.speakingPart3 * timeEstimates.speakingPart3) /
      (cardsByType.speakingPart1 + cardsByType.speakingPart2 + cardsByType.speakingPart3)
    : 0;

  const dailyNewMinutes = Math.round(
    dailyNewSpeaking * avgSpeakingTime +
    dailyNewWriting * timeEstimates.writing
  );

  // 复习时间（从 todayDue 获取）
  const dailyDueMinutes = todayDue.dueMinutes;

  // 每日总时间
  const dailyMinutes = dailyNewMinutes + dailyDueMinutes;

  // 每日各类型卡片数
  const dailyCardsByType: Partial<Record<CardType, number>> = {
    speaking: dailyNewSpeaking + todayDue.speakingDue,
    writing: dailyNewWriting + todayDue.writingDue,
  };

  // 进度百分比（基于首次学习）
  const progressPercent = totalCards > 0 ? Math.round((completedCards / totalCards) * 100) : 0;

  // 理想进度（从创建日期到目标日期的线性进度）
  const totalDays = Math.max(1, Math.ceil((goal.targetDate - goal.createdAt) / (24 * 60 * 60 * 1000)));
  const elapsedDays = Math.ceil((now - goal.createdAt) / (24 * 60 * 60 * 1000));
  const idealProgressPercent = Math.min(100, Math.round((elapsedDays / totalDays) * 100));

  // 进度差异（天数）
  const actualDaysWorth = totalDays * (progressPercent / 100);
  const daysDiff = Math.round(actualDaysWorth - elapsedDays);

  return {
    totalCards,
    cardsByType,
    firstPassMinutes,
    reviewMinutes,
    totalMinutes,
    daysLeft,
    dailyNewCards,
    dailyDueCards,
    dailyCards,
    dailyNewMinutes,
    dailyDueMinutes,
    dailyMinutes,
    dailyCardsByType,
    completedCards,
    progressPercent,
    idealProgressPercent,
    daysDiff,
  };
}

/**
 * 获取今日学习进度
 */
export async function getTodayProgress(
  goal: StudyGoal,
  db: MyIELTSDatabase = defaultDb
): Promise<TodayProgress> {
  const allCards = await db.cards.toArray();
  const { scope } = goal;

  // 今日开始时间戳
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayTs = todayStart.getTime();

  let totalCards = 0;
  let totalMinutes = 0;
  const byType: Partial<Record<CardType, number>> = {};

  for (const card of allCards) {
    if (!card.history || card.history.length === 0) continue;

    // 检查卡片是否在目标范围内
    let inScope = false;
    if (card.type === 'speaking' && scope.speaking.enabled) {
      const part = (card.content as Record<string, unknown>)?.part;
      if (scope.speaking.parts.includes(part as 'Part 1' | 'Part 2' | 'Part 3')) {
        inScope = true;
      }
    } else if (card.type === 'writing' && scope.writing.enabled) {
      inScope = true;
    } else if (card.type === 'listening' && scope.listening.enabled) {
      inScope = true;
    } else if (card.type === 'vocab' && scope.vocab.enabled) {
      inScope = true;
    }

    if (!inScope) continue;

    // 筛选今日的学习记录
    const todayEntries = card.history.filter(h => h.ts >= todayTs);
    if (todayEntries.length === 0) continue;

    // 统计卡片数
    totalCards += todayEntries.length;
    byType[card.type] = (byType[card.type] ?? 0) + todayEntries.length;

    // 估算学习时间
    for (const entry of todayEntries) {
      const isReview = card.history.indexOf(entry) > 0;
      let minutes = 0;

      if (card.type === 'speaking') {
        const part = (card.content as Record<string, unknown>)?.part;
        if (part === 'Part 1') minutes = goal.timeEstimates.speakingPart1;
        else if (part === 'Part 2') minutes = goal.timeEstimates.speakingPart2;
        else if (part === 'Part 3') minutes = goal.timeEstimates.speakingPart3;
        else minutes = goal.timeEstimates.speakingPart1;
      } else if (card.type === 'writing') {
        minutes = goal.timeEstimates.writing;
      } else if (card.type === 'listening') {
        minutes = goal.timeEstimates.listening;
      } else if (card.type === 'vocab') {
        minutes = goal.timeEstimates.vocab;
      }

      // 复习时间按类型计算
      if (isReview) {
        const ratio = card.type === 'speaking' 
          ? goal.reviewSettings.speaking.reviewRatio 
          : goal.reviewSettings.writing.reviewRatio;
        minutes = Math.round(minutes * ratio);
      }

      totalMinutes += minutes;
    }
  }

  return { cards: totalCards, minutes: totalMinutes, byType };
}

/**
 * 格式化分钟为可读字符串
 */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}分钟`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}小时${mins}分` : `${hours}小时`;
}

/**
 * 格式化剩余天数
 */
export function formatDaysLeft(targetDate: number, now: number = Date.now()): string {
  const days = Math.ceil((targetDate - now) / (24 * 60 * 60 * 1000));
  if (days < 0) return '已过期';
  if (days === 0) return '今天';
  if (days === 1) return '明天';
  return `${days}天`;
}

/**
 * 格式化进度状态
 */
export function formatProgressStatus(daysDiff: number): string {
  if (daysDiff === 0) return '进度正常';
  if (daysDiff > 0) return `超前${daysDiff}天`;
  return `落后${Math.abs(daysDiff)}天`;
}
